import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, ArrowRight, Trash2 } from "lucide-react";
import ConfirmDialog from "@/components/ConfirmDialog";

export default function StoresPage() {
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", address: "", phone: "", email: "", manager_name: "", team_members: [], team_input: "" });

  const reload = async () => {
    try {
      const { data } = await api.get("/stores");
      setStores(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const addTeamMember = () => {
    const name = (form.team_input || "").trim();
    if (!name) return;
    if (form.team_members.includes(name)) return;
    setForm({ ...form, team_members: [...form.team_members, name], team_input: "" });
  };
  const removeTeamMember = (name) => setForm({ ...form, team_members: form.team_members.filter(n => n !== name) });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { team_input, ...payload } = form;
      await api.post("/stores", payload);
      toast.success("Store created");
      setOpen(false);
      setForm({ name: "", code: "", address: "", phone: "", email: "", manager_name: "", team_members: [], team_input: "" });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async (s) => {
    await api.put(`/stores/${s.id}`, { active: !s.active });
    toast.success(s.active ? "Deactivated" : "Activated"); reload();
  };

  const askDelete = (s) => setPendingDelete(s);
  const confirmDelete = async () => {
    const s = pendingDelete;
    if (!s) return;
    try { await api.delete(`/stores/${s.id}`); toast.success("Store deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setPendingDelete(null); }
  };

  return (
    <div className="space-y-8" data-testid="stores-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div><div className="overline">Network</div><h1 className="font-display text-4xl sm:text-5xl mt-2">Stores</h1></div>
        {user.role === "admin" && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-store-btn"><Plus className="w-4 h-4 mr-2"/>New store</Button></DialogTrigger>
          <DialogContent className="max-h-[90vh] flex flex-col p-0 gap-0">
            <DialogHeader className="px-6 pt-6 pb-3 shrink-0">
              <DialogTitle className="font-display">Add bookstore</DialogTitle>
              <DialogDescription className="sr-only">Register a new store in the network.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="flex flex-col min-h-0 flex-1">
              <div className="overflow-y-auto px-6 pb-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Name</Label><Input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} required data-testid="store-name"/></div>
                  <div><Label>Code</Label><Input value={form.code} onChange={e=>setForm({...form, code:e.target.value.toUpperCase()})} required data-testid="store-code"/></div>
                </div>
                <div><Label>Address</Label><Input value={form.address} onChange={e=>setForm({...form, address:e.target.value})}/></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Phone</Label><Input value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})}/></div>
                  <div><Label>Email</Label><Input type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})}/></div>
                </div>
                <div><Label>Manager name</Label><Input value={form.manager_name} onChange={e=>setForm({...form, manager_name:e.target.value})}/></div>

                <div className="border border-border rounded-sm p-4 space-y-3" data-testid="inventory-team-box">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">Inventory team</Label>
                    <p className="text-xs text-muted-foreground mt-1">People responsible for keeping this store's inventory up to date.</p>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add a team member name…"
                      value={form.team_input}
                      onChange={e=>setForm({...form, team_input:e.target.value})}
                      onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTeamMember(); } }}
                      data-testid="team-member-input"
                    />
                    <Button type="button" variant="outline" onClick={addTeamMember} data-testid="team-member-add">Add</Button>
                  </div>
                  {form.team_members.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1" data-testid="team-member-chips">
                      {form.team_members.map(n => (
                        <span key={n} className="inline-flex items-center gap-1.5 text-xs bg-secondary text-secondary-foreground px-2.5 py-1 rounded-full">
                          {n}
                          <button type="button" onClick={()=>removeTeamMember(n)} className="text-muted-foreground hover:text-destructive" aria-label={`Remove ${n}`}>×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter className="px-6 py-4 border-t border-border shrink-0 bg-background">
                <Button type="submit" data-testid="store-submit">Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {loading && stores.length === 0 && Array.from({ length: 6 }).map((_, i) => (
          <div key={`sk-${i}`} className="grid-border-card p-6 space-y-4 animate-pulse" data-testid="store-card-skeleton">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1 space-y-2">
                <div className="h-3 w-14 bg-muted rounded-sm"/>
                <div className="h-5 w-3/4 bg-muted rounded-sm"/>
              </div>
              {user.role === "admin" && <div className="h-6 w-10 bg-muted rounded-full"/>}
            </div>
            <div className="space-y-2">
              <div className="h-3 w-full bg-muted rounded-sm"/>
              <div className="h-3 w-1/3 bg-muted rounded-sm"/>
              <div className="h-3 w-1/2 bg-muted rounded-sm"/>
            </div>
            <div className="h-3 w-28 bg-muted rounded-sm"/>
          </div>
        ))}
        {stores.map(s => (
          <div key={s.id} className="grid-border-card p-6 space-y-4" data-testid="store-card">
            <div className="flex items-start justify-between gap-3">
              <Link to={`/stores/${s.id}`} className="min-w-0 flex-1 group" data-testid={`store-link-${s.code}`}>
                <div className="overline">{s.code}</div>
                <h3 className="font-display text-xl mt-1 truncate group-hover:text-accent transition-colors">{s.name}</h3>
              </Link>
              {user.role === "admin" && <Switch checked={s.active} onCheckedChange={() => toggleActive(s)} data-testid={`toggle-store-${s.code}`}/>}
            </div>
            <div className="text-sm space-y-1 text-muted-foreground">
              <div className="truncate">{s.address || "—"}</div>
              <div className="font-mono text-xs">{s.phone || "—"}</div>
              <div className="truncate">{s.manager_name || "—"}</div>
            </div>
            {(s.team_members || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid={`store-team-preview-${s.code}`}>
                {(s.team_members || []).slice(0, 4).map(n => (
                  <span key={n} className="text-[10px] uppercase tracking-widest bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full">{n}</span>
                ))}
                {(s.team_members || []).length > 4 && (
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">+{s.team_members.length - 4}</span>
                )}
              </div>
            )}
            <Link to={`/stores/${s.id}`} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-accent hover:underline pt-2">
              {user.role === "admin" ? "Manage logins & contact" : "More Info"} <ArrowRight className="w-3 h-3"/>
            </Link>
            {user.role === "admin" && (
            <div className="flex justify-end -mt-2">
              <Button variant="ghost" size="icon" onClick={()=>askDelete(s)} data-testid={`delete-store-${s.code}`} aria-label="Delete store">
                <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive"/>
              </Button>
            </div>
            )}
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o)=>{ if(!o) setPendingDelete(null); }}
        title={`Delete store "${pendingDelete?.name}"?`}
        description={`This permanently removes the store, wipes every inventory row for it, and deactivates its staff logins. Type the store code to confirm.`}
        confirmLabel="Delete store"
        destructive
        requireText={pendingDelete?.code || ""}
        onConfirm={confirmDelete}
        testid="confirm-delete-store"
      />
    </div>
  );
}
